const BigNumber = require('bignumber.js');
const Branch = require('@/lib/Branches/Branch');
const Company = require('@/lib/Companies/Company');
const Customer = require('@/lib/Customers/Customer');
const Device = require('@/lib/Devices/Device');
const DocumentNumber = require('@/lib/Orders/DocumentNumbers/DocumentNumber');
const DocumentTypeEnum = require('@/lib/Orders/DocumentNumbers/DocumentTypeEnum');
const FieldGroup = require('@/lib/CustomFields/FieldGroup');
const FieldGroupCollection = require('@/lib/CustomFields/FieldGroupCollection');
const FieldLevel = require('@/lib/CustomFields/FieldLevel');
const FulfillmentMethodEnum = require('@/lib/Orders/FulfillmentMethodEnum');
const OrderFulfillment = require('@/libraries/DigiTickets/Fulfillments/OrderFulfillment');
const OrderFulfillmentInstructions = require('@/lib/Orders/OrderFulfillmentInstructions');
const OrderLine = require('@/libraries/DigiTickets/Orders/OrderLine');
const Payment = require('@/lib/Payments/Payment');
const PaymentCalculator = require('@/libraries/DigiTickets/Payment/PaymentCalculator');
const PaymentChannel = require('@/lib/Channels/PaymentChannel');
const PaymentMethod = require('@/lib/PaymentMethods/PaymentMethod');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const SalesTotals = require('@/lib/Calculation/SalesTotals');
const User = require('@/lib/Users/User');
const { bookingExceededVatThreshold } = require('@/lib/Tax/Countries/UK');
const { container } = require('@/container');
const { createFieldGroupCollectionFromOrder } = require('@/lib/CustomFields/fieldGroupCollectionFactory');
const { createMissingItemInstances, fixInstanceFieldDataNumbering } = require('@/libraries/DigiTickets/Cart/cartLineCleaning');
const { getDocumentNumberFromOrder } = require('@/lib/Orders/DocumentNumbers');
const { hydrate } = require('@/lib/Hydration');
const { setOrderLinesValidUntil } = require('@/libraries/DigiTickets/Orders/calculateValidUntil');
const { toBool, toDate, toFloat, toInt, toNullableBool, toNullableInt, toNullableString } = require('@/functions/transform');

/**
 * @param {object} [data]
 *
 * @constructor
 */
const Order = function (data = {}) {
    /**
     * Simple name/values
     */
    this.bookingRef = null;

    /**
     * @type {String}
     */
    this.originalRef = null;

    /**
     * ID of the original order (if this is an adjustment, and if this was returned by the API).
     * This may be populated as the same as this.ID if this is the original order.
     *
     * @type {?number}
     */
    this.originalOrderID = null;

    this.cancelledAt = null;

    /**
     * @type {boolean}
     */
    this.confirmed = true;

    /**
     * @type {?Date}
     */
    this.confirmedAt = null;

    this.date = new Date();
    this.deviceID = null;

    this.giftAid = null;

    /**
     * @type {Number}
     */
    this.ID = null;

    /**
     * The ID of the branch that created this order.
     * This might be different from the branch each the items in the order are from.
     * In some cases it can be null.
     *
     * @type {number|null}
     */
    this.orderBranchID = null;

    /**
     * The Branch that created this order.
     * This might be different from the branch each the items in the order are from.
     * In some cases it can be null.
     *
     * @type {Branch|null}
     */
    this.orderBranch = null;

    this.notes = null;
    this.paid = null;
    this.paidAt = null;

    /**
     * @type {boolean}
     */
    this.receiptEmailed = false;

    /**
     * @type {boolean}
     */
    this.receiptPrinted = false;
    this.receiptPrinting = false;

    /**
     * Is this order fully redeemed?
     * This might be null if we don't know. It will become null when an offline redemption happens.
     *
     * @type {boolean|null}
     */
    this.redeemed = false;

    /**
     * Date the order was full redeemed.
     * This should be checked in combination with checking this.redeemed is not null to see if the value can
     * still be considered valid.
     *
     * @type {?Date}
     */
    this.redemptionDate = null;

    this.redemptionPrivileges = null;

    /**
     * The API does funny things with the order lines and the order total.
     * The 'total' is overwritten as the sum of all bookings in an order (aka orders after adjustments), and only
     * counts confirmed non-cancelled orders.
     * Use the getTotal() method to return the appropriate total.
     *
     * @type {number}
     */
    this.total = 0;

    /**
     * The raw total field of this specific order.
     * This will be populated for all orders including unconfirmed and cancelled orders.
     * Use the getTotal() method to return the appropriate total.
     *
     * @type {number}
     */
    this.thisOrderTotal = 0;

    /**
     * A human readable reference entered by the user for this order (e.g. "Table 123").
     * Gets shown on the kitchen screen, and printed on the receipt.
     *
     * @type {string|null}
     */
    this.staffRef = null;

    /**
     * @type {Payment[]}
     */
    this.payments = [];

    this.remainingBalance = 0;
    this.fullyPaid = false;
    this.overpaid = false;
    this.partiallyPaid = false;
    this.unpaid = false;
    this.hasPendingPayments = false;

    /**
     * @deprecated
     *
     * This property should no longer be used, but it is left in in case custom receipts or
     * somewhere unknown is using it.
     * It's not reliable because some orders, such as those that are returned by the API after
     * making an order adjustment, do not have a company set on them.
     *
     * @type {Company}
     */
    this.company = null;

    /**
     * @type {Customer}
     */
    this.customer = null;

    /**
     * @type {User|null}
     */
    this.seller = null;

    /**
     * @type {number|null}
     */
    this.sellersUserID = null;

    /**
     * Field data after it has been processed and organised.
     * Order/Event and Item level data is combined here.
     *
     * @type {FieldGroupCollection}
     */
    this.fields = new FieldGroupCollection();

    /**
     * @type {OrderFulfillment[]}
     */
    this.fulfillments = [];

    /**
     * TODO: Temporary name because we can't remove this.fields currently because it is used in receipt templates stored
     * in app. Eventually this will just be called 'fields' to match Cart.
     *
     * @type {FieldGroup}
     */
    this.orderLevelFieldGroup = new FieldGroup({ level: FieldLevel.ORDER });

    /**
     * @type {OrderLine[]}
     */
    this.items = [];

    /**
     * In afterHydration this will be populated with all the memberships contained in this order.
     *
     * @type {Membership[]}
     */
    this.memberships = [];

    /**
     * Where was the order created (Online, Admin Area, Point of sale, Resellers)
     *
     * @type {?PaymentChannel}
     */
    this.paymentChannel = null;

    /**
     * @type {SalesTotals}
     */
    this.totals = new SalesTotals();

    /**
     * @type {?string}
     */
    this.thirdPartyID = null;

    /**
     * @type {?string}
     */
    this.thirdPartyRef = null;

    /**
     * @type {Date|null}
     */
    this.purgeFromCustomerScreenAt = null;

    /**
     * Determines if the order's ref is visible or hidden on the kitchen-customer screen.
     *
     * @type {boolean}
     */
    this.visibleOnCustomerScreen = false;

    /**
     * If additional document numbers were loaded for this order this will be an array of
     * DocumentNumber objects, or an empty array if there were none.
     * If document numbers were not loaded this will be null.
     *
     * @type {DocumentNumber[]|null}
     */
    this.documentNumbers = null;

    /**
     * How the order will be received by the customer, if the order was loaded with the
     * orders.fulfillmentInstructions resolver. If they were not loaded this will be null.
     *
     * @type {OrderFulfillmentInstructions|null}
     */
    this.fulfillmentInstructions = null;

    /**
     * This will be true if the order was loaded with the 'includeBookingTotals' flag from the API
     * (which is the case when loading a single order but not querying many orders),
     * and if the booking totals say that the sequence of orders making up this booking ever had a total of >= £250.
     *
     * Since PROP-1214 the order is loaded from the API when you open the order details page (if you are online),
     * so this will be populated if you're online and on the order details page.
     *
     * @type {boolean|null}
     */
    this.exceededVatLimit = null;

    /**
     * @type {TaxSummary}
     */
    this.taxSummary = null;

    /**
     * When an order is loaded on the order details page the bookingTotals will also be requested.
     * This gives a running total for each order in the booking sequence.
     *
     * @type {?{bookingRef: string, orderID: number, runningTotal: number, total: number}[]}
     */
    this.bookingTotals = null;

    /**
     * Tax data for Greek tax machines.
     *
     * It will be null if it has not been checked/loaded.
     * It will be a GreekTaxLine instance if applicable (this is a Greek client and the value has been loaded).
     * Otherwise it will be false if it is not applicable to this client (all non-Greek clients).
     *
     * @type {GreekTaxLine|false|null}
     */
    this.greekTaxLine = null;

    /**
     * If populated, these URLs will be printed at the bottom of the receipt in a QR code.
     * This is currently only used for Greek tax. The URL is that of an invoice generated by Entersoft.
     * There's no reason this couldn't be used for other purposes in the future.
     *
     * @type {string[]}
     */
    this.receiptQRCodeUrls = [];

    /**
     * If populated, these strings will be printed as-is at the bottom of the receipt.
     *
     * @type {string[]}
     */
    this.additionalReceiptText = [];

    /**
     * @type {boolean}
     */
    this.groupBooking = false;

    hydrate(data, this);
};

Order.prototype = {
    getHydrationMap() {
        return {
            bookingRef: toNullableString,
            bookingTotals: {},
            originalRef: toNullableString,
            cancelledAt: toDate,
            company: {
                field: ['company', 'companies'],
                model: Company,
            },
            confirmed: toBool,
            confirmedAt: toDate,
            customer: {
                field(data) {
                    // The orders endpoint can return address and contact data on the top level. e.g. order.street
                    // Therefore if we don't have a customer pass all the data through, so we can check and extract.
                    return data.customer ? data.customer : data;
                },
                model: Customer,
            },
            date: toDate,
            device: {
                model: Device,
            },
            deviceID: toNullableInt,
            documentNumbers: {
                modelCollection: DocumentNumber,
            },
            email: {},
            exceededVatLimit: toNullableBool,
            fulfillmentInstructions: {
                model: OrderFulfillmentInstructions,
            },
            fulfillments: {
                modelCollection: OrderFulfillment,
            },
            giftAid: toBool,
            groupBooking: toBool,
            ID: {
                field: ['ID', 'orderID'],
                transform: toInt,
            },
            items: {
                field: ['orderitems', 'items'],
                modelCollection: OrderLine,
            },
            notes: {},
            orderBranch: {
                model: Branch,
            },
            orderBranchID: toInt,
            orderLevelFieldGroup: {
                model: FieldGroup,
            },
            originalOrderID: toNullableInt,
            paid: {
                field: ['paid', 'paidAt'],
                transform: toBool,
            },
            paidAt: toDate,
            paymentChannel: {
                field(data) {
                    const paymentChannel = data.paymentChannel ?? data.paymentchannel;
                    if (paymentChannel || !data.paymentChannelID) {
                        return paymentChannel;
                    }

                    // Orders pushed to the kitchen screen over the websocket only have the paymentChannelID,
                    // so create the paymentChannel from that.
                    return PaymentChannel.fromID(data.paymentChannelID);
                },
                model: PaymentChannel,
            },
            paymentMethod: {
                // TODO This is from when there could only be one payment for an order.
                // This has been replaced by the payments array. Check if it's still used and remove it.
                field: ['paymentmethods', 'paymentMethod'],
                model: PaymentMethod,
            },
            payments: {
                field(data) {
                    return data.isHydrated ? data.payments : data.paymentlines;
                },
                modelCollection: Payment,
            },
            postcode: {},
            receiptQRCodeUrls: {},
            redeemed: toBool,
            redemptionDate: toDate,
            redemptionPrivileges: {},
            seller: {
                field: ['seller', 'sellers'], // TODO: Remove 'sellers' after PROP-835
                model: User,
            },
            sellersUserID: toNullableInt,
            staffRef: {},
            tel: {},
            thirdPartyID: {
                field: ['thirdPartyID', '3rdPartyID'],
                transform: toNullableString,
            },
            thirdPartyRef: toNullableString,
            thisOrderTotal: toFloat,
            total: toFloat,
            totals: {
                model: SalesTotals,
            },
        };
    },

    /**
     * @param {object} data
     */
    afterHydration(data) {
        const orderFieldInstancesFromApiBuilder = container.orderFieldInstancesFromApiBuilder;

        // If the API sends an order without the correct resolvers we might be missing item instances.
        // This seems to be the case when orders destined for the kitchen screen are loaded.
        // In this case, create the missing instances so they exist even if they don't have any data. We're about to
        // add field data to them below.
        if (data.orderitems && data.orderitems[0] && !data.orderitems[0].iteminstances) {
            this.items.forEach((line) => {
                createMissingItemInstances(line);
            });
        }

        // Create a sequential lineNumber for each line and remember which ordersItemsID has which line number.
        let lineNumbers = {};
        this.items.forEach((line, index) => {
            line.lineNumber = index + 1; // Uses a 1-based index to match field data line numbering.
            lineNumbers[line.ID] = line.lineNumber;
            fixInstanceFieldDataNumbering(line);
        });

        // From the API data.fielddata is an array containing *all* the field data for the order - both order and
        // item level. Additionally each line in data.ordersitems has a 'fielddata' that is data only relating to that
        // line. ProPoint has always looked at the former for order level data and the latter for item level data.
        // Let's keep it working the same way.
        let fieldData = [];

        // Get the order level field data from the root level.
        if (data.hasOwnProperty('fielddata')) {
            fieldData = fieldData.concat(
                ...data.fielddata.filter((fd) => (fd.fields && fd.fields.level === 'order'))
            );
        }

        // Get the item level field data from each line.
        if (data.orderitems) {
            data.orderitems.forEach((orderLine) => {
                if (orderLine.fielddata) {
                    fieldData = fieldData.concat(...orderLine.fielddata);
                }
            });
        }

        // If we found any field data add it to the order.
        if (fieldData.length > 0) {
            // Create an array of FieldInstances containing data for the order.
            let fieldInstances = orderFieldInstancesFromApiBuilder.createFieldInstancesFromApiFieldData(
                fieldData,
                lineNumbers
            );

            // Set those FieldInstances in the appropriate places on the order:
            // order level instances in the order level FieldGroup
            // item level instances in the FieldGroup on the appropriate ItemInstance on the appropriate line.
            orderFieldInstancesFromApiBuilder.setFieldInstancesOnOrder(fieldInstances, this);
        }

        // Set the old style this.fields to a FieldGroupCollection for backward compatibility.
        // It's used in receipt templates.
        this.fields = createFieldGroupCollectionFromOrder(this);

        this.memberships = this.extractMemberships();

        if (this.bookingTotals) {
            this.exceededVatLimit = bookingExceededVatThreshold(this.bookingTotals);
        }

        this.calculate();
    },

    /**
     * @param {Payment} payment
     */
    addPayment: function (payment) {
        this.payments.push(payment);
        this.calculate();
    },

    calculate: function calculate() {
        this.totals.reset();

        for (let i = 0; i < this.items.length; ++i) {
            this.totals.addLine(this.items[i]);
        }

        this.setRemainingBalanceAndPaidStates();

        setOrderLinesValidUntil(this);
    },

    /**
     * @returns {string|null}
     */
    getOriginalBookingRef() {
        return (this.originalRef || this.bookingRef) || null;
    },

    /**
     * The total cost of the order.
     *
     * @return {number}
     */
    getTotal: function () {
        if (!this.isConfirmed()) {
            // The API's total is the aggregate for the whole booking and that aggregate only counts confirmed
            // orders, so it is 0 here. thisOrderTotal is this order's own recorded total.
            return this.thisOrderTotal;
        }

        return this.total;
    },

    /**
     * Add up the amount from each paid payment on the order.
     * This uses amount not tendered because tendered for cash payments may be higher than the amount. Change will
     * have already been given, so we don't care about that difference anymore, only the amount is what we need.
     *
     * @return {number}
     */
    getTotalPaid: function () {
        return PaymentCalculator.getTotalAmount(this.payments.filter((payment) => payment.paid));
    },

    /**
     * Return the total of pending GoCardless payments.
     * When we pay for an order using GoCardless paid = 0 so these payments are excluded from getTotalPaid()
     *
     * @return {number}
     */
    getTotalPendingGoCardless: function () {
        return PaymentCalculator.getTotalAmount(
            this.payments.filter((payment) => {
                return payment.paymentMethod?.ref === PaymentMethodRef.GOCARDLESS
                    && payment.pending
                    && !payment.paid;
            })
        );
    },

    /**
     * Returns the total paid for the order, which is all completed payments plus any pending
     * payments that we trust to complete (GoCardless).
     *
     * @returns {number}
     */
    getTotalPaidIncludingTrustedPending() {
        return this.getTotalPaid() + this.getTotalPendingGoCardless();
    },

    /**
     * Sometimes an order is a deposit so the order is not always fully paid.
     *
     * @return {number}
     */
    getRemainingBalance: function () {
        return new BigNumber(this.getTotal())
            .minus(this.getTotalPaidIncludingTrustedPending())
            .toNumber();
    },

    /**
     * Set the remaining balance and the various 'paid' properties. This is called either when the order lines change or
     * when a payment is added to an order after an adjustment.
     */
    setRemainingBalanceAndPaidStates() {
        let totalPaid = this.getTotalPaid();

        this.remainingBalance = this.getRemainingBalance();

        this.fullyPaid = this.remainingBalance === 0;
        this.partiallyPaid = totalPaid > 0 && this.remainingBalance > 0;
        this.overpaid = this.remainingBalance < 0;
        this.unpaid = this.remainingBalance > 0;
        this.hasPendingPayments = this.payments.some((p) => p.pending && !p.paid);
    },

    get fulfillmentCreatedAt() {
        // Get the most recent fulfillment
        if (this.fulfillments.length === 0) {
            return null;
        }

        const dates = this.fulfillments.map((fulfillment) => new Date(fulfillment.createdAt).getTime());
        const latestCreatedAt = Math.max(...dates);

        return new Date(latestCreatedAt);
    },

    get fulfilledAt() {
        if (this.fulfillments.length === 0) {
            return null;
        }
        const unfulfilled = this.fulfillments.filter((f) => !f.isFulfilled());
        if (unfulfilled.length > 0) {
            return null;
        }
        const dates = this.fulfillments.map((fulfillment) => new Date(fulfillment.fulfilledAt).getTime());
        const fulfilledAt = Math.max(...dates);

        return new Date(fulfilledAt);
    },

    isFulfilled: function isFulfilled() {
        return this.fulfilledAt !== null;
    },

    setCustomer: function setCustomer(customer) {
        this.customer = customer;
    },

    firstItemDetails: function firstItemDetails() {
        let firstItem = this.items[0];
        return firstItem.qty + ' x ' + firstItem.name;
    },

    /**
     * @returns {OrderLine[]}
     */
    getItems: function getItems() {
        return this.items;
    },

    /**
     * @param {OrderLine} line
     *
     * @return {Order}
     */
    addLine(line) {
        this.items.push(line);

        return this;
    },

    /**
     * Extract all the Memberships in this order.
     *
     * @returns {Membership[]}
     */
    extractMemberships: function extractMemberships() {
        let memberships = [];

        for (let i = 0; i < this.items.length; i++) {
            let line = this.items[i];

            for (let j = 0; j < line.iteminstances.length; j++) {
                let instance = line.iteminstances[j];
                if (instance.membership) {
                    memberships.push(instance.membership);
                }
            }
        }

        return memberships;
    },

    /**
     * Get a list of Members on this order.
     *
     * @returns {Member[] | []}
     */
    getMembers: function getMembers() {
        let orderMembers = [];

        this.items.forEach(
            /** @param {OrderLine} orderLine */
            function (orderLine) {
                orderMembers = [].concat(orderMembers, orderLine.getMembers());
            }
        );

        return orderMembers;
    },

    /**
     * Return a line from the cart by its line number.
     *
     * @param {number|string} lineNumber
     *
     * @returns {?CartLine}
     */
    getLineByNumber(lineNumber) {
        lineNumber = parseInt(lineNumber, 10);
        return this.items.find((line) => line.lineNumber === lineNumber) || null;
    },

    /**
     * @return {FieldGroup}
     */
    getOrderLevelFieldGroup() {
        return this.orderLevelFieldGroup;
    },

    /**
     * @param {Payment[]} payments
     */
    setPayments(payments) {
        this.payments = payments;
        this.setRemainingBalanceAndPaidStates();
    },

    /**
     * Getter to make Order compatible with Cart when getting lines.
     * For compatibility with Cart entity.
     *
     * @return {OrderLine[]}
     */
    getLines() {
        return this.items;
    },

    /**
     * @param {number} ordersItemsID
     *
     * @return {OrderLine|null}
     */
    getLineByID(ordersItemsID) {
        return this.items.find((line) => line.ID === ordersItemsID) || null;
    },

    /**
     * Return an array of unique itemIDs in the order.
     *
     * @return {Number[]}
     */
    getItemIDs() {
        const itemIDs = new Set();
        this.getLines().forEach((orderLine) => itemIDs.add(orderLine.getItemID()));

        return Array.from(itemIDs);
    },

    /**
     * Returns the total number of items in the cart (sum of the qty per line)
     */
    getTotalQty() {
        return this.getLines().reduce((totalQty, line) => totalQty + line.getQuantity(), 0);
    },

    /**
     * Check if this order contains any items that should be displayed on the kitchen screen.
     *
     * @return {boolean}
     */
    containsKitchenItems() {
        return !!this.items.find((i) => {
            const item = i.getItem();
            // TODO: To handle adjustment orders removing lines the qty check might need to be removed.
            return item && item.showOnKitchenScreen && i.qty > 0;
        });
    },

    /**
     * Returns true if this order has any payments taken more than 5 minutes after the order date.
     *
     * @return {boolean}
     */
    hasPaymentsAfterOrderDate() {
        const orderDate = new Date(this.date).getTime();
        const maxDifference = 5 * 60 * 1000; // 5 minutes in milliseconds

        // Check if any of the payments' takenAt dates are more than 5 minutes after the order date.
        for (let i = 0; i < this.payments.length; i++) {
            if (this.payments[i].takenAt) {
                const paymentDate = new Date(this.payments[i].takenAt).getTime();
                if (paymentDate - orderDate > maxDifference) {
                    return true;
                }
            }
        }

        return false;
    },

    /**
     * @return {boolean}
     */
    hasDataForTaxReceipt() {
        return !!this.taxSummary?.taxSummaryGroupsByTaxRate;
    },

    /**
     * @returns {boolean}
     */
    hasItems() {
        for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].qty !== 0) {
                return true;
            }
        }

        return false;
    },

    /**
     * True for both cancelled and voided orders, because voiding an order also sets cancelledAt.
     * Use isVoided to tell the two apart.
     *
     * @returns {boolean}
     */
    isCancelled() {
        return !!this.cancelledAt;
    },

    /**
     * A voided order was cancelled before it was ever confirmed.
     * Unlike a cancelled order there is no adjustment order reversing its lines, because an unconfirmed
     * order never counted towards stock, session spaces or reporting in the first place.
     *
     * @returns {boolean}
     */
    isVoided() {
        return !this.isConfirmed() && this.isCancelled();
    },

    /**
     * @returns {boolean}
     */
    isConfirmed() {
        return !!this.confirmed;
    },

    /**
     * An order that is confirmed and not cancelled. It's a live real sale and can be acted upon
     * (redeemed, refunded, etc).
     *
     * @returns {boolean}
     */
    isLive() {
        return this.isConfirmed() && !this.isCancelled();
    },

    /**
     * @returns {string}
     */
    getOriginalRef() {
        return this.originalRef || this.bookingRef;
    },

    /**
     * @param {string} url
     */
    addReceiptQRCodeUrl(url) {
        if (!this.receiptQRCodeUrls.includes(url)) {
            this.receiptQRCodeUrls.push(url);
        }
    },

    /**
     * @returns {boolean|null}
     */
    nonZeroCreditNoteHasBeenIssued() {
        if (!this.bookingTotals) {
            // No info loaded. Can't be sure, but can't say no either.
            return null;
        }

        // We'll use the handy bookingTotals object to check if a credit note has been issued.
        for (let i = 0; i < this.bookingTotals.length; i++) {
            // If any order in the sequence had a total < 0 then a credit note was issued.
            if (this.bookingTotals[i].total < 0) {
                return true;
            }
        }

        return false;
    },

    /**
     * @returns {boolean}
     */
    isAdjustment() {
        return !!(this.originalOrderID && this.originalOrderID !== this.ID);
    },

    /**
     * The collection number for an F&B order e.g. "007".
     * No prefix is added here, the display number already includes the app's own prefix and padding.
     *
     * @return {string|null}
     */
    get displayCollectionNumber() {
        const docNumber = getDocumentNumberFromOrder(
            this,
            DocumentTypeEnum.ORDER_COLLECTION_NUMBER
        );

        return docNumber ? docNumber.displayNumber : null;
    },

    /**
     * Returns how the order will be received by the customer.
     * If the order has no fulfillmentInstructions (such as an order built from the cart on the sell screen) they are
     * worked out the same way the API does for orders without stored instructions: a staffRef means table service at
     * the table named by the staffRef, otherwise it is collection.
     *
     * TODO: https://digitickets.atlassian.net/browse/PROP-2581 ProPoint should start building the specific
     * instructions and stop doing the "if staffRef" check.
     *
     * @returns {OrderFulfillmentInstructions}
     */
    getFulfillmentInstructions() {
        if (this.fulfillmentInstructions) {
            return this.fulfillmentInstructions;
        }

        if (this.staffRef) {
            return new OrderFulfillmentInstructions({
                fulfillmentMethod: FulfillmentMethodEnum.TABLE_SERVICE,
                tableName: this.staffRef,
            });
        }

        return new OrderFulfillmentInstructions({
            fulfillmentMethod: FulfillmentMethodEnum.COLLECTION,
            collectionNumber: this.displayCollectionNumber,
        });
    },
};

module.exports = Order;
